/** Small SVG charts, drawn against the console's own tokens (styles.css
 *  .viz). No chart library: these are the few shapes the operations screens
 *  need, each with a hover readout, a legend when there is more than one
 *  series, and nothing loud but the data. */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

// ── sizing ────────────────────────────────────────────────────
function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((e) => setW(Math.max(200, e[0].contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** Clean axis ticks: 0, and round steps up to the maximum. */
function ticks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(Math.round(v * 100) / 100);
  if (out[out.length - 1] < max) out.push(out[out.length - 1] + step);
  return out;
}

const fmtN = (v: number) =>
  Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${Math.round(v * 10) / 10}`;

export function Legend({ items }: { items: Array<{ label: string; color: string; kind?: "line" | "box" | "step" }> }) {
  return (
    <div className="viz-legend">
      {items.map((i) => (
        <span key={i.label}>
          <i className={i.kind ?? "line"} style={{ background: i.kind === "step" ? undefined : i.color, borderColor: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

// ── line chart with a crosshair ───────────────────────────────
export type Series = {
  key: string;
  label: string;
  color: string;
  values: Array<number | null>;
  /** a wash under the line */
  area?: boolean;
  /** drawn as steps - for a capacity that changes by the hour */
  step?: boolean;
  /** thin and quiet - for a reference, not a result */
  quiet?: boolean;
};

export function LineChart({
  x,
  series,
  height = 190,
  now,
  xLabel,
  valueSuffix = "",
}: {
  /** ISO times, one per point */
  x: string[];
  series: Series[];
  height?: number;
  /** ISO time of "now", drawn as a marker */
  now?: string;
  xLabel?: (iso: string) => string;
  valueSuffix?: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { l: 38, r: 10, t: 10, b: 22 };
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;
  const max = Math.max(1, ...series.flatMap((s) => s.values.map((v) => v ?? 0)));
  const yt = ticks(max);
  const top = yt[yt.length - 1];
  const n = Math.max(1, x.length - 1);
  const X = (i: number) => pad.l + (i / n) * w;
  const Y = (v: number) => pad.t + h - (v / top) * h;
  const label = xLabel ?? ((iso: string) => new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric" }));

  const paths = useMemo(
    () =>
      series.map((s) => {
        let d = "";
        s.values.forEach((v, i) => {
          if (v == null) return;
          const px = X(i);
          const py = Y(v);
          if (!d) d = `M${px},${py}`;
          else if (s.step) d += `H${px}V${py}`;
          else d += `L${px},${py}`;
        });
        const last = s.values.map((v, i) => (v == null ? -1 : i)).filter((i) => i >= 0);
        const area =
          s.area && last.length
            ? `${d}L${X(last[last.length - 1])},${Y(0)}L${X(last[0])},${Y(0)}Z`
            : null;
        return { s, d, area };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, width, height, top],
  );

  // one label per hour (on the hour), thinned to fit the width
  const onHour = x.map((t, i) => (new Date(t).getUTCMinutes() === 0 ? i : -1)).filter((i) => i >= 0);
  const labelled = onHour.length >= 2 ? onHour : x.map((_, i) => i);
  const room = Math.max(2, Math.floor(w / 58));
  const stride = Math.max(1, Math.ceil(labelled.length / room));
  const shown = new Set(labelled.filter((_, k) => k % stride === 0));
  const nowIdx = now ? x.findIndex((t) => t >= now) : -1;

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const box = (e.currentTarget as SVGRectElement).getBoundingClientRect();
    const rel = (e.clientX - box.left) / box.width;
    setHover(Math.max(0, Math.min(x.length - 1, Math.round(rel * n))));
  };

  return (
    <div className="viz" ref={ref} style={{ position: "relative" }}>
      <svg width={width} height={height} role="img">
        {yt.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={pad.l + w} y1={Y(t)} y2={Y(t)} className="viz-grid" />
            <text x={pad.l - 6} y={Y(t) + 3.5} className="viz-axis" textAnchor="end">
              {fmtN(t)}
            </text>
          </g>
        ))}
        {x.map((t, i) =>
          shown.has(i) ? (
            <text key={t} x={X(i)} y={height - 5} className="viz-axis" textAnchor="middle">
              {label(t)}
            </text>
          ) : null,
        )}
        {nowIdx > 0 && (
          <g>
            <rect x={pad.l} y={pad.t} width={X(nowIdx) - pad.l} height={h} className="viz-past" />
            <line x1={X(nowIdx)} x2={X(nowIdx)} y1={pad.t} y2={pad.t + h} className="viz-now" />
            <text x={X(nowIdx) + 4} y={pad.t + 9} className="viz-axis">now</text>
          </g>
        )}
        {paths.map(({ s, area }) =>
          area ? <path key={`${s.key}-a`} d={area} fill={s.color} opacity={0.1} /> : null,
        )}
        {paths.map(({ s, d }) => (
          <path
            key={s.key}
            d={d}
            fill="none"
            stroke={s.color}
            strokeWidth={s.quiet ? 1.5 : 2}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={s.quiet ? 0.85 : 1}
          />
        ))}
        {hover != null && (
          <g>
            <line x1={X(hover)} x2={X(hover)} y1={pad.t} y2={pad.t + h} className="viz-cross" />
            {series.map((s) =>
              s.values[hover] == null ? null : (
                <circle key={s.key} cx={X(hover)} cy={Y(s.values[hover] as number)} r={4} fill={s.color} className="viz-dot" />
              ),
            )}
          </g>
        )}
        <rect
          x={pad.l}
          y={pad.t}
          width={w}
          height={h}
          fill="transparent"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {hover != null && (
        <div
          className="viz-tip"
          style={{ left: Math.min(X(hover) + 12, width - 190), top: pad.t }}
        >
          <div className="viz-tip-h">{new Date(x[hover]).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })}</div>
          {series.map((s) => (
            <div key={s.key} className="viz-tip-r">
              <i style={{ background: s.color }} />
              <b>{s.values[hover] == null ? "—" : `${fmtN(s.values[hover] as number)}${valueSuffix}`}</b>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── columns, one per minute ───────────────────────────────────
export function Columns({
  items,
  height = 110,
}: {
  items: Array<{ key: string; value: number; color: string; tip: ReactNode }>;
  height?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { l: 30, r: 4, t: 6, b: 4 };
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;
  const max = Math.max(1, ...items.map((i) => i.value));
  const yt = ticks(max, 2);
  const top = yt[yt.length - 1];
  const slot = w / Math.max(1, items.length);
  const bw = Math.min(24, Math.max(2, slot - 2));
  return (
    <div className="viz" ref={ref} style={{ position: "relative" }}>
      <svg width={width} height={height}>
        {yt.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={pad.l + w} y1={pad.t + h - (t / top) * h} y2={pad.t + h - (t / top) * h} className="viz-grid" />
            <text x={pad.l - 5} y={pad.t + h - (t / top) * h + 3.5} className="viz-axis" textAnchor="end">
              {fmtN(t)}
            </text>
          </g>
        ))}
        {items.map((it, i) => {
          const bh = Math.max(it.value > 0 ? 2 : 0, (it.value / top) * h);
          const x0 = pad.l + i * slot + (slot - bw) / 2;
          const y0 = pad.t + h - bh;
          const r = Math.min(4, bw / 2, bh);
          return (
            <g key={it.key} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
              <rect x={pad.l + i * slot} y={pad.t} width={slot} height={h} fill="transparent" />
              <path
                d={`M${x0},${y0 + bh}V${y0 + r}Q${x0},${y0} ${x0 + r},${y0}H${x0 + bw - r}Q${x0 + bw},${y0} ${x0 + bw},${y0 + r}V${y0 + bh}Z`}
                fill={it.color}
                opacity={hover == null || hover === i ? 1 : 0.55}
              />
            </g>
          );
        })}
      </svg>
      {hover != null && items[hover] && (
        <div className="viz-tip" style={{ left: Math.min(pad.l + hover * slot + 10, width - 220), top: 0 }}>
          {items[hover].tip}
        </div>
      )}
    </div>
  );
}

// ── one bar split into parts ──────────────────────────────────
export function StackBar({ parts }: { parts: Array<{ label: string; value: number; color: string; detail?: string }> }) {
  const total = parts.reduce((a, p) => a + p.value, 0);
  const [hover, setHover] = useState<number | null>(null);
  if (total <= 0) return <div className="empty" style={{ padding: 16 }}>Nothing is due.</div>;
  return (
    <div className="viz">
      <div className="viz-stack">
        {parts.map((p, i) =>
          p.value > 0 ? (
            <div
              key={p.label}
              style={{ flex: p.value, background: p.color, opacity: hover == null || hover === i ? 1 : 0.55 }}
              onPointerEnter={() => setHover(i)}
              onPointerLeave={() => setHover(null)}
              title={`${p.label}: ${p.value.toLocaleString("en-US")}${p.detail ? ` · ${p.detail}` : ""}`}
            />
          ) : null,
        )}
      </div>
      <div className="viz-legend" style={{ marginTop: 8 }}>
        {parts.map((p) => (
          <span key={p.label}>
            <i className="box" style={{ background: p.color }} />
            <b className="mono">{p.value.toLocaleString("en-US")}</b> {p.label}
            {p.detail && <em className="dim"> · {p.detail}</em>}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── a figure's small trend ────────────────────────────────────
export function Sparkline({ values, color = "var(--series-1)", height = 26 }: { values: number[]; color?: string; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  if (values.length < 2) return <div ref={ref} style={{ height }} />;
  const max = Math.max(1, ...values);
  const X = (i: number) => (i / (values.length - 1)) * (width - 4) + 2;
  const Y = (v: number) => height - 3 - (v / max) * (height - 6);
  const d = values.map((v, i) => `${i ? "L" : "M"}${X(i)},${Y(v)}`).join("");
  const last = values[values.length - 1];
  return (
    <div ref={ref} style={{ height }}>
      <svg width={width} height={height}>
        <path d={`${d}L${X(values.length - 1)},${height}L${X(0)},${height}Z`} fill={color} opacity={0.1} />
        <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
        <circle cx={X(values.length - 1)} cy={Y(last)} r={3} fill={color} className="viz-dot" />
      </svg>
    </div>
  );
}

// ── how full the next hour is ─────────────────────────────────
export function LoadMeter({
  waiting,
  coming,
  capacity,
}: {
  waiting: number;
  coming: number;
  capacity: number;
}) {
  const total = waiting + coming;
  const scale = Math.max(capacity * 1.3, total, 1);
  const pct = (v: number) => `${Math.min(100, (v / scale) * 100)}%`;
  const over = total > capacity;
  return (
    <div className="viz">
      <div className="viz-meter">
        <div className="seg" style={{ width: pct(waiting), background: over ? "var(--warn)" : "var(--series-1)" }} />
        <div className="seg soft" style={{ width: pct(coming), background: over ? "var(--warn)" : "var(--series-1)" }} />
        {capacity > 0 && <div className="cap" style={{ left: pct(capacity) }} />}
      </div>
      <div className="viz-legend" style={{ marginTop: 7 }}>
        <span><i className="box" style={{ background: over ? "var(--warn)" : "var(--series-1)" }} /><b className="mono">{Math.round(waiting)}</b> waiting now</span>
        <span><i className="box soft" style={{ background: over ? "var(--warn)" : "var(--series-1)" }} /><b className="mono">{Math.round(coming)}</b> on the way</span>
        <span><i className="line" style={{ background: "var(--ink)" }} /><b className="mono">{Math.round(capacity)}</b> the team can take</span>
      </div>
    </div>
  );
}
